import { useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { useCurrency } from '../currency/CurrencyProvider';
import {
  useAjusteInventario,
  useCatalogosProducto,
  useCrearSucursal,
  useInventario,
  useKardex,
  useProductos,
  useSucursales,
} from '../api/queries-finanzas';
import { CatalogosPanel } from '../components/CatalogosPanel';
import { FinanceDemoNotice, TabBar } from '../components/FinanceBits';
import { Modal } from '../components/Modal';
import { ProductoModal } from '../components/ProductoModal';
import { PlusIcon } from '../components/icons';
import { useToast } from '../ui/ToastProvider';
import { formatDate, formatMoney } from '../lib/format';
import type { InventarioItem } from '../api/types';

export function InventarioPage() {
  const { user, demo } = useAuth();
  const { formatMoney: displayMoney } = useCurrency();
  const { notify } = useToast();
  const [tab, setTab] = useState<'stock' | 'productos' | 'catalogos' | 'almacenes'>('stock');
  const [search, setSearch] = useState('');
  const [openProducto, setOpenProducto] = useState(false);
  const [openAlmacen, setOpenAlmacen] = useState(false);
  const [kardexId, setKardexId] = useState<string | null>(null);
  const [ajusteProducto, setAjusteProducto] = useState<InventarioItem | null>(null);
  const inventario = useInventario(search);
  const productos = useProductos(search);
  const sucursales = useSucursales();
  const catalogos = useCatalogosProducto();
  const crearSucursal = useCrearSucursal();
  const kardex = useKardex(kardexId);
  const ajuste = useAjusteInventario();
  const [cantidad, setCantidad] = useState('');
  const [motivo, setMotivo] = useState('');
  const [almacenForm, setAlmacenForm] = useState({ codigo: '', nombre: '', direccion: '' });
  const rows = inventario.data?.data ?? [];
  const productRows = productos.data?.data ?? [];
  const almacenes = sucursales.data ?? [];
  const canAdjust = !demo && user !== null && ['admin', 'supervisor'].includes(user.rol);
  const valorInventario = rows.reduce((sum, row) => sum + row.stockActual * row.costoPromedioUsd, 0);
  const bajoStock = rows.filter((row) => row.bajoStock).length;
  const servicios = productRows.filter((producto) => producto.tipo === 'SERVICIO').length;

  const submitAjuste = async () => {
    if (!ajusteProducto) return;
    const qty = Number(cantidad);
    if (!qty || !motivo.trim()) {
      notify('Indica una cantidad distinta de cero y un motivo.', 'warning');
      return;
    }
    try {
      await ajuste.mutateAsync({ productoId: ajusteProducto.id, cantidad: qty, motivo: motivo.trim() });
      notify(`Inventario de ${ajusteProducto.nombre} ajustado.`, 'success');
      setAjusteProducto(null);
      setCantidad('');
      setMotivo('');
    } catch (error) {
      notify(error instanceof Error ? error.message : 'No se pudo ajustar el inventario.', 'error');
    }
  };

  const submitAlmacen = async () => {
    if (!almacenForm.codigo.trim() || !almacenForm.nombre.trim()) {
      notify('Código y nombre del almacén son obligatorios.', 'warning');
      return;
    }
    try {
      await crearSucursal.mutateAsync({
        codigo: almacenForm.codigo.trim(),
        nombre: almacenForm.nombre.trim(),
        ...(almacenForm.direccion.trim() ? { direccion: almacenForm.direccion.trim() } : {}),
      });
      notify(`Almacén ${almacenForm.nombre.trim()} creado.`, 'success');
      setOpenAlmacen(false);
      setAlmacenForm({ codigo: '', nombre: '', direccion: '' });
    } catch (error) {
      notify(error instanceof Error ? error.message : 'No se pudo crear el almacén.', 'error');
    }
  };

  return (
    <div className="dashboard">
      <header className="page-head">
        <div>
          <p className="eyebrow">Inventario</p>
          <h1 className="page-title">Inventario, productos y almacenes</h1>
          <p className="page-sub">Stock, costo promedio, catálogo y almacenes de la operación.</p>
        </div>
        {tab === 'productos' || tab === 'almacenes' ? (
          <button
            type="button"
            className="btn btn--primary"
            disabled={!canAdjust}
            onClick={() => (tab === 'productos' ? setOpenProducto(true) : setOpenAlmacen(true))}
          >
            <PlusIcon size={17} /> {tab === 'productos' ? 'Nuevo producto o servicio' : 'Nuevo almacén'}
          </button>
        ) : null}
      </header>

      <FinanceDemoNotice demo={demo} />

      <div className="summary-strip summary-strip--4">
        <div className="summary-tile">
          <span>Valor del inventario</span>
          <strong>{displayMoney(valorInventario)}</strong>
          <small>Costo promedio actual</small>
        </div>
        <div className="summary-tile summary-tile--warning">
          <span>Productos bajo mínimo</span>
          <strong>{bajoStock}</strong>
          <small>Requieren reposición</small>
        </div>
        <div className="summary-tile summary-tile--accent">
          <span>Artículos</span>
          <strong>{rows.length}</strong>
          <small>Con control de inventario</small>
        </div>
        <div className="summary-tile">
          <span>Servicios</span>
          <strong>{servicios}</strong>
          <small>Sin control de stock</small>
        </div>
      </div>

      <div className="page-tabs-row">
        <TabBar
          label="Inventario, productos, catálogos o almacenes"
          value={tab}
          onChange={setTab}
          tabs={[
            { value: 'stock', label: 'Stock & Kardex' },
            { value: 'productos', label: 'Productos & Servicios' },
            { value: 'catalogos', label: 'Catálogos' },
            { value: 'almacenes', label: 'Almacenes' },
          ]}
        />
      </div>

      {tab === 'stock' ? (
      <section className="card clients-card">
        <div className="clients-toolbar">
          <div className="search-input">
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar producto, SKU o categoría…"
              aria-label="Buscar inventario"
            />
          </div>
          <span className="pill pill--soft">{rows.length} productos</span>
        </div>
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Producto / SKU</th>
                <th>Categoría</th>
                <th className="align-right">Stock</th>
                <th className="align-right">Mínimo</th>
                <th className="align-right">Costo prom.</th>
                <th className="align-right">Valor</th>
                <th>Estado</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {inventario.isLoading ? <tr><td colSpan={8}><div className="table-empty">Cargando inventario…</div></td></tr> : null}
              {!inventario.isLoading && rows.length === 0 ? <tr><td colSpan={8}><div className="table-empty">Sin productos de inventario.</div></td></tr> : null}
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <div className="doc-cell">
                      <strong>{row.nombre}</strong>
                      <span>{row.sku} · {row.unidad}</span>
                    </div>
                  </td>
                  <td>{row.categoria}</td>
                  <td className="align-right">
                    <strong>{row.stockActual.toLocaleString('es-VE')}</strong>
                  </td>
                  <td className="align-right">{row.stockMinimo.toLocaleString('es-VE')}</td>
                  <td className="align-right">{formatMoney(row.costoPromedioUsd, 'USD')}</td>
                  <td className="align-right">{formatMoney(row.stockActual * row.costoPromedioUsd, 'USD')}</td>
                  <td>
                    <span className={`status-badge status-badge--${row.bajoStock ? 'warning' : 'success'}`}>
                      {row.bajoStock ? 'Bajo mínimo' : 'Disponible'}
                    </span>
                  </td>
                  <td>
                    <div className="row-actions">
                      <button type="button" className="btn btn--outline btn--sm" onClick={() => setKardexId(row.id)}>
                        Kardex
                      </button>
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        disabled={!canAdjust}
                        onClick={() => setAjusteProducto(row)}
                      >
                        Ajustar
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      ) : null}

      {tab === 'productos' ? (
        <section className="card clients-card">
          <div className="clients-toolbar">
            <div className="search-input">
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar producto, SKU o categoría…"
                aria-label="Buscar productos y servicios"
              />
            </div>
            <span className="pill pill--soft">{productRows.length} productos y servicios</span>
          </div>
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Producto / SKU</th>
                  <th>Tipo</th>
                  <th>Categoría</th>
                  <th className="align-right">Precio</th>
                  <th className="align-right">IVA</th>
                  <th className="align-right">Stock</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {productos.isLoading ? <tr><td colSpan={7}><div className="table-empty">Cargando productos…</div></td></tr> : null}
                {!productos.isLoading && productRows.length === 0 ? <tr><td colSpan={7}><div className="table-empty">Sin productos registrados.</div></td></tr> : null}
                {productRows.map((producto) => (
                  <tr key={producto.id}>
                    <td>
                      <div className="doc-cell">
                        <strong>{producto.nombre}</strong>
                        <span>{producto.sku} · {producto.unidad}</span>
                      </div>
                    </td>
                    <td><span className="pill pill--soft">{producto.tipo ?? 'ARTICULO'}</span></td>
                    <td>{producto.categoria}</td>
                    <td className="align-right">{formatMoney(producto.precioUsd, 'USD')}</td>
                    <td className="align-right">{producto.impuestoPct}%</td>
                    <td className="align-right">
                      {producto.controlaInventario ? (producto.stockActual ?? 0).toLocaleString('es-VE') : '—'}
                    </td>
                    <td>
                      <span className={`status-badge status-badge--${producto.activo ? 'success' : 'neutral'}`}>
                        {producto.activo ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {tab === 'catalogos' ? <CatalogosPanel canManage={canAdjust} /> : null}

      {tab === 'almacenes' ? (
        <section className="card clients-card">
          <div className="clients-toolbar">
            <span className="pill pill--soft">{almacenes.length} almacenes</span>
          </div>
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr><th>Código</th><th>Almacén</th><th>Dirección</th><th>Estado</th></tr>
              </thead>
              <tbody>
                {sucursales.isLoading ? <tr><td colSpan={4}><div className="table-empty">Cargando almacenes…</div></td></tr> : null}
                {!sucursales.isLoading && almacenes.length === 0 ? <tr><td colSpan={4}><div className="table-empty">Sin almacenes registrados.</div></td></tr> : null}
                {almacenes.map((almacen) => (
                  <tr key={almacen.id}>
                    <td><span className="mono">{almacen.codigo}</span></td>
                    <td><strong>{almacen.nombre}</strong></td>
                    <td>{almacen.direccion ?? '—'}</td>
                    <td><span className={`status-badge status-badge--${almacen.activo === false ? 'neutral' : 'success'}`}>{almacen.activo === false ? 'Inactivo' : 'Activo'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {kardexId && kardex.data ? (
        <Modal
          title={kardex.data.producto.nombre}
          eyebrow={`Kardex · ${kardex.data.producto.sku}`}
          onClose={() => setKardexId(null)}
          wide
          footer={<button type="button" className="btn btn--outline" onClick={() => setKardexId(null)}>Cerrar</button>}
        >
          <div className="modal-card__body">
            <div className="summary-strip">
              <div className="summary-tile"><span>Stock actual</span><strong>{kardex.data.producto.stockActual}</strong></div>
              <div className="summary-tile"><span>Costo promedio</span><strong>{formatMoney(kardex.data.producto.costoPromedioUsd, 'USD')}</strong></div>
              <div className="summary-tile"><span>Mínimo</span><strong>{kardex.data.producto.stockMinimo}</strong></div>
            </div>
            <div className="table-scroll">
              <table className="data-table data-table--compact">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Tipo</th>
                    <th>Origen</th>
                    <th className="align-right">Cantidad</th>
                    <th className="align-right">Costo unit.</th>
                    <th className="align-right">Stock después</th>
                  </tr>
                </thead>
                <tbody>
                  {kardex.data.movimientos.map((movement) => (
                    <tr key={movement.id}>
                      <td>{formatDate(movement.fecha)}</td>
                      <td>
                        <span className={`status-badge status-badge--${movement.tipo === 'ENTRADA' ? 'success' : 'warning'}`}>
                          {movement.tipo}
                        </span>
                      </td>
                      <td>{movement.origen}{movement.referencia ? ` · ${movement.referencia}` : ''}</td>
                      <td className="align-right">{movement.cantidad}</td>
                      <td className="align-right">{formatMoney(movement.costoUnitarioUsd, 'USD')}</td>
                      <td className="align-right">{movement.stockDespues}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Modal>
      ) : null}

      {ajusteProducto ? (
        <Modal
          title="Ajuste de inventario"
          eyebrow={ajusteProducto.nombre}
          onClose={() => setAjusteProducto(null)}
          footer={
            <>
              <button type="button" className="btn btn--outline" onClick={() => setAjusteProducto(null)}>Cancelar</button>
              <button type="button" className="btn btn--primary" onClick={() => void submitAjuste()} disabled={ajuste.isPending}>
                {ajuste.isPending ? 'Ajustando…' : 'Aplicar ajuste'}
              </button>
            </>
          }
        >
          <div className="modal-card__body">
            <p className="muted">
              Usa cantidad positiva para aumentar stock y negativa para disminuirlo. El ajuste
              queda en el kardex y en auditoría.
            </p>
            <div className="form-grid">
              <label className="field">
                <span className="field-label">Cantidad (+/-) *</span>
                <input className="input" inputMode="decimal" value={cantidad} onChange={(e) => setCantidad(e.target.value)} placeholder="Ej. 10 o -5" />
              </label>
              <label className="field">
                <span className="field-label">Stock actual</span>
                <input className="input" value={ajusteProducto.stockActual} disabled />
              </label>
              <label className="field field--wide">
                <span className="field-label">Motivo *</span>
                <input className="input" value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Conteo físico, merma, corrección…" />
              </label>
            </div>
          </div>
        </Modal>
      ) : null}

      {openProducto ? (
        <ProductoModal
          categorias={catalogos.data?.categorias ?? []}
          unidades={catalogos.data?.unidades ?? []}
          onClose={() => setOpenProducto(false)}
          onCreated={(nombre) => notify(`Producto ${nombre} creado.`, 'success')}
        />
      ) : null}

      {openAlmacen ? (
        <Modal
          title="Nuevo almacén"
          eyebrow="Inventario"
          onClose={() => setOpenAlmacen(false)}
          footer={
            <>
              <button type="button" className="btn btn--outline" onClick={() => setOpenAlmacen(false)}>Cancelar</button>
              <button type="button" className="btn btn--primary" onClick={() => void submitAlmacen()} disabled={crearSucursal.isPending}>
                {crearSucursal.isPending ? 'Guardando…' : 'Crear almacén'}
              </button>
            </>
          }
        >
          <div className="modal-card__body">
            <div className="form-grid">
              <label className="field"><span className="field-label">Código *</span><input className="input" value={almacenForm.codigo} onChange={(e) => setAlmacenForm({ ...almacenForm, codigo: e.target.value })} placeholder="ALM-01" /></label>
              <label className="field"><span className="field-label">Nombre *</span><input className="input" value={almacenForm.nombre} onChange={(e) => setAlmacenForm({ ...almacenForm, nombre: e.target.value })} placeholder="Almacén principal" /></label>
              <label className="field field--wide"><span className="field-label">Dirección</span><input className="input" value={almacenForm.direccion} onChange={(e) => setAlmacenForm({ ...almacenForm, direccion: e.target.value })} /></label>
            </div>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
